import { type Opportunity, generateProjectSummary } from "@repo/api";
import { type BadgeProps } from "@repo/skapa";
import { t } from "@repo/translation";
import { useToast } from "@repo/ui";
import { getDebugMessage } from "@repo/utils";
import { useEffect, useRef, useState } from "react";

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

export const isSummaryStale = (summaryGeneratedAt: string) => {
  return (
    new Date().getTime() - new Date(summaryGeneratedAt).getTime() >
    THREE_DAYS_MS
  );
};

export const useSummaryHeaderConfig = (opportunity: Opportunity) => {
  const lastGeneratedAt = opportunity.aiProjectData?.summaryGeneratedAt;
  const projectLastUpdatedAt = opportunity.updatedAt;

  // If summary has never been generated, allow manual generation
  if (!lastGeneratedAt) {
    return {
      canGenerate: true,
      text: t("prompt.generate_new_summary_for_new_project"),
      color: "yellow" as BadgeProps["colour"],
    };
  }

  // If summary is stale (generated more than 3 days ago), allow manual generation
  if (isSummaryStale(lastGeneratedAt)) {
    return {
      canGenerate: true,
      text: t("prompt.summary_stale"),
      color: "red" as BadgeProps["colour"],
    };
  }

  // If project has been updated since last summary generation, allow manual generation
  if (
    projectLastUpdatedAt !== undefined &&
    new Date(lastGeneratedAt).getTime() <
      new Date(projectLastUpdatedAt).getTime()
  ) {
    return {
      canGenerate: true,
      text: t("prompt.generate_new_summary_for_updated_project"),
      color: "orange" as BadgeProps["colour"],
    };
  }

  // If summary was generated within the last 3 hours, disallow regeneration to prevent abuse
  if (
    new Date().getTime() - new Date(lastGeneratedAt).getTime() >
    THREE_HOURS_MS
  ) {
    return {
      canGenerate: true,
      text: t("prompt.summary_generated_recently"),
      color: "green" as BadgeProps["colour"],
    };
  }

  return {
    canGenerate: false,
    text: t("prompt.summary_generated_recently"),
    color: "green" as BadgeProps["colour"],
  };
};

export const useAutoGenerateProjectSummary = (opportunity: Opportunity) => {
  const { updateToast } = useToast();

  const [isGenerating, setIsGenerating] = useState(false);
  const generatingFromTimestamp = useRef<string | null | undefined>(undefined);

  // Track the latest mounted opportunity ID to ignore stale async failures.
  const currentOpportunityIdRef = useRef(opportunity.id);

  useEffect(() => {
    currentOpportunityIdRef.current = opportunity.id;
  }, [opportunity.id]);

  // Clear loading state when summaryGeneratedAt changes (server has finished generating)
  useEffect(() => {
    const currentTimestamp = opportunity.aiProjectData?.summaryGeneratedAt;
    const fromTimestamp = generatingFromTimestamp.current;

    if (
      isGenerating &&
      fromTimestamp !== undefined &&
      currentTimestamp !== fromTimestamp &&
      // For auto-generation (from=null), only complete when we get a real timestamp back
      (fromTimestamp !== null || currentTimestamp != null)
    ) {
      setIsGenerating(false);
      generatingFromTimestamp.current = undefined;
    }
  }, [isGenerating, opportunity.aiProjectData?.summaryGeneratedAt]);

  const handleGenerate = async () => {
    const requestedOpportunityId = opportunity.id;
    generatingFromTimestamp.current =
      opportunity.aiProjectData?.summaryGeneratedAt ?? null;
    setIsGenerating(true);
    try {
      await generateProjectSummary(requestedOpportunityId);
    } catch (e) {
      // Guard: only update state if this response is for the current opportunity
      if (currentOpportunityIdRef.current === requestedOpportunityId) {
        setIsGenerating(false);
        generatingFromTimestamp.current = undefined;
        updateToast({
          id: requestedOpportunityId,
          title: t("prompt.failed_to_generate_summary"),
          message: getDebugMessage(e),
          type: "error",
        });
      }
    }
  };

  return { isGenerating, handleGenerate };
};
